import React, { useContext, useEffect, useRef, useState } from "react";
import localizationStrings from "../../statics/localization";
import { SessionContext } from "../../context/SessionContext";
import Notification from "../notification";
import {
  changeProfileParentalLevel,
  parentalControlApi,
  getUserInitialInfo,
  modifyProfile,
  getProfilesApi,
  getParentalLevel
} from "../../api/user/user";
import { useHistory } from "react-router-dom";
import PasswordModal from "../passwordModal";
import UnselectIcon from "../../assets/icons/svg/UnselectIcon";
import SelectedIcon from "../../assets/icons/svg/SelectedIcon";
import ParentalSelectingIcon from "../../assets/icons/svg/ParentalSelectingIcon";
import ReturnIcon from "../../assets/icons/svg/ReturnIcon";
import BigLockIcon from "../../assets/icons/svg/BigLockIcon";
import { flowCopy } from "../../utils/flowCopy";
import useMountedRef from "../../utils/useMountedRef";
import AtlasLogo from "../../assets/logo/atlas-logo.png";

const ParentalControl = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [showNoPinNotification, setShowNoPinNotification] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userInfo, setUserInfo_] = useState(null);
  const [askAdminPin, setAskAdminPin] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [persistedLevel, setPersistedLevel] = useState("");
  const {
    profile,
    adminProfile,
    setUserInfo,
    userFrameConfig,
    setProfile,
    setAdminProfile,
    setAllProfiles
  } = useContext(SessionContext);
  const mounted = useMountedRef();
  const gateVersion = useRef(0);
  const savingRef = useRef(false);
  const verifiedPin = useRef(null);
  const [saving, setSaving] = useState(false);
  const [gateProfile, setGateProfile] = useState(null);
  const [authorizedId, setAuthorizedId] = useState(null);
  const [error, setError] = useState(false);
  const [gateRetry, setGateRetry] = useState(0);
  const authorized = authorizedId === profile?.id && !!authorizedId;
  const selectedUserLevel = useRef(null);
  const history = useHistory();

  let currentProfile = JSON.parse(localStorage.getItem("profile"));

  const parentalContols = [
    {
      userlevel: "7+",
      title: "7+",
      description: localizationStrings.parentalControlText.sevenPlus
    },
    {
      userlevel: "13+",
      title: "13+",
      description: localizationStrings.parentalControlText.thirteenPlus
    },
    {
      userlevel: "18+",
      title: "18+",
      description: localizationStrings.parentalControlText.allAge
    },
    {
      userlevel: "G",
      title: localizationStrings.parentalControlText.generalTitle,
      description: localizationStrings.parentalControlText.general
    }
  ];

  const clickConfirm = async () => {
    if (!authorized || savingRef.current || !selectedUserLevel.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(false);
    const version = gateVersion.current;
    try {
      const response = await parentalControlApi(selectedUserLevel.current, verifiedPin.current, profile.id);
      if (response.status !== 200) throw new Error("Parental update failed");
      const profilesData = await getProfilesApi();
      if (!mounted.current || version !== gateVersion.current) return;
      if (profilesData.status !== 200) throw new Error("Profile refresh failed");
      const profiles = profilesData.data;
      const item = profiles.find(el => el.id === profile.id);
      setAllProfiles(profiles);
      if (item) {
        setProfile(item);
        localStorage.setItem("profile", JSON.stringify(item));
        if (item.main) setAdminProfile(item);
      }
      localStorage.setItem("profiles", JSON.stringify(profiles));
      history.push("/dashboard/settings");
    } catch (e) {
      if (mounted.current) setError(true);
    } finally {
      savingRef.current = false;
      if (mounted.current) { setSaving(false); setShowNotification(false); }
    }
  };

  const clickNotConfirm = () => {
    setSelectedOption(null);
    setShowNotification(false);
  };

  const goProfileSettings = () => {
    setShowNoPinNotification(false);
    if (gateProfile) history.push(`/profile/${gateProfile.id || gateProfile.profilecode}?page=1`);
  };

  const getUserInfo = async () => {
    const { data } = await getUserInitialInfo();
    if (data.returncode === "0") {
      setUserInfo_(data);
      setUserInfo(data);
    }
  };

  const normalizeLevel = (value) => {
    if (value === undefined || value === null) return "";
    const str = String(value).trim();
    if (str.toUpperCase() === "G") return "G";
    return str.replace(/\+/g, "");
  };

  const renderedItems = parentalContols.map((item) => {
    let selectIcon;
    if (selectedOption === item.userlevel) {
      selectIcon = <ParentalSelectingIcon />; // temporary selection (orange)
    } else if (
      normalizeLevel(item.userlevel) === normalizeLevel(persistedLevel)
    ) {
      selectIcon = <SelectedIcon />; // persisted selection
    } else {
      selectIcon = <UnselectIcon />;
    }

    return (
      <button
        type="button"
        aria-pressed={
          normalizeLevel(selectedOption || persistedLevel) ===
          normalizeLevel(item.userlevel)
        }
        disabled={!authorized || saving || (askAdminPin && item.userlevel === "18+")}
        key={item.userlevel}
        onClick={() => {
          setSelectedOption(item.userlevel);
          selectedUserLevel.current = item.userlevel.replace(/\+/g, "");
          setShowNotification(true);
        }}
        className="parentalcontrol-card-wrapper"
      >
        <div className="parentalcontrol-card">
          <div className="pc-card-body">
            <div className="pc-card-selector">{selectIcon}</div>
            <div className="pc-card-texts">
              <div className="pc-card-title">{item.title}</div>
              <div className="pc-card-subtitle">{item?.description}</div>
            </div>
          </div>
        </div>
      </button>
    );
  });

  useEffect(() => {
    const version = ++gateVersion.current;
    setAuthorizedId(null);
    verifiedPin.current = null;
    setGateProfile(null);
    setShowPasswordModal(false);
    setShowNoPinNotification(false);
    setError(false);
    setPersistedLevel("");
    setSelectedOption(null);
    const child = profile?.kid === true || profile?.kid === 1 || profile?.kid === "1" ||
      (!!userFrameConfig?.Kids_Profile_Id && profile?.profileroleid === userFrameConfig.Kids_Profile_Id);
    setAskAdminPin(child);
    const resolveGate = async () => {
      try {
        let target = child ? adminProfile : profile;
        if (child && !target) {
          const response = await getProfilesApi();
          target = response.data?.find(item => item.main === true || item.main === 1 || item.main === "1");
        }
        if (!mounted.current || version !== gateVersion.current) return;
        if (!target || !(target.id || target.profilecode)) throw new Error("Missing parent profile");
        const level = await getParentalLevel(profile.id);
        if (!mounted.current || version !== gateVersion.current) return;
        setPersistedLevel(level.data);
        setGateProfile(target);
        const hasPin = target.hasPin === true || target.hasPin === 1 || target.hasPin === "1" || target.havepwd === "1";
        setShowPasswordModal(hasPin);
        setShowNoPinNotification(!hasPin);
      } catch (e) {
        if (mounted.current && version === gateVersion.current) setError(true);
      }
    };
    resolveGate();
    return () => { gateVersion.current++; verifiedPin.current = null; };
  }, [profile?.id, profile?.kid, profile?.hasPin, profile?.havepwd, profile?.profileroleid, adminProfile?.id, adminProfile?.hasPin, adminProfile?.havepwd, userFrameConfig?.Kids_Profile_Id, gateRetry]);

  return (
    <div className="settings-container">
      <div
        className="settings-icon"
        style={{
          position: "fixed",
          left: `${(33 / 1920) * 100}vw`,
          top: `calc(${(72 / 1920) * 100}vw + ${(49 / 1920) * 100}vw)`,
          zIndex: 5,
          margin: 0,
          gap: "10px"
        }}
        onClick={() => {
          history.push(`/dashboard/settings`);
        }}
      >
        <ReturnIcon />
        <div className="icon-settings">{localizationStrings.back}</div>
      </div>
      <div className="pc-header">
        <img className="pc-logo" src={AtlasLogo} alt="Atlas" />
        <div className="pc-title">{localizationStrings.parentalControl}</div>
      </div>
      {error && <div role="alert">{flowCopy().loadError} <button onClick={() => setGateRetry(value => value + 1)}>{flowCopy().retry}</button></div>}
      <div className="parental-control-container">{renderedItems}</div>
      {showNotification ? (
        <Notification
          text={localizationStrings.parentalControlText.changeText}
          confirmText={localizationStrings.yes}
          notConfirmText={localizationStrings.no}
          theme="dark"
          onClickConfirm={clickConfirm}
          onClickNotConfirm={clickNotConfirm}
        />
      ) : null}

      {showPasswordModal ? (
        <Notification
          text={
            askAdminPin
              ? localizationStrings.askAdminPin
              : localizationStrings.askProfilePin
          }
          confirmText={localizationStrings.enter}
          onClickConfirm={() => {}}
          showCloseButton={true}
          onClickNotConfirm={() => {
            setShowPasswordModal(false);
            history.push(`/dashboard/settings`);
          }}
          isPassword={true}
          persistPin={false}
          icon={<BigLockIcon />}
          sendResult={(val, pin) => {
            if (!val) {
              history.push(`/dashboard/settings`);
            }
            if (val && /^\d{4}$/.test(pin || "")) {
              verifiedPin.current = pin;
              setAuthorizedId(profile.id);
            }
            setShowPasswordModal(false);
          }}
          profileCode={gateProfile?.id || gateProfile?.profilecode}
        />
      ) : null}
      {showNoPinNotification ? (
        <Notification
          text={localizationStrings.createProfilePinCodeText}
          confirmText={localizationStrings.createProfilePinCode}
          notConfirmText={localizationStrings.cancel}
          onClickConfirm={goProfileSettings}
          onClickNotConfirm={() => {
            setShowNoPinNotification(false);
            history.push(`/dashboard/settings`);
          }}
        />
      ) : null}
    </div>
  );
};

export default ParentalControl;
